import { ProductAttributeType, ProductAttributeValueType } from './base'

/**
 * Enum for operations allowed on product attributes.
 */
export enum ProductAttributeFilterOperator {
  eq = 'eq',
  ne = 'ne',
  gt = 'gt',
  lt = 'lt',
  gte = 'gte',
  lte = 'lte',
  contains = 'contains',
  not_contains = 'not_contains',
  between = 'between',
  starts_with = 'starts_with',
  not_starts_with = 'not_starts_with',
  ends_with = 'ends_with',
  not_ends_with = 'not_ends_with',
  empty = 'empty',
  not_empty = 'not_empty',
  one_of = 'one_of',
  desc_of_one_of = 'desc_of_one_of'
}

/**
 * Default display names for filter operations.
 */
const PRODUCT_ATTRIBUTE_FILTER_OPERATOR_DISPLAY_MAP: Record<
  ProductAttributeFilterOperator,
  string
> = {
  eq: 'Is',
  ne: 'Is not',
  gt: 'Greater than',
  lt: 'Less than',
  gte: 'Greater than or equals',
  lte: 'Less than or equals',
  contains: 'Contains',
  not_contains: 'Does not contain',
  between: 'Is between',
  starts_with: 'Starts with',
  not_starts_with: 'Does not start with',
  ends_with: 'Ends with',
  not_ends_with: 'Does not end with',
  empty: 'Is empty',
  not_empty: 'Is not empty',
  one_of: 'Is one of',
  desc_of_one_of: 'Descendant of one of'
}

/**
 * Abbreviated display names for filter operations in the top-level filter.
 */
const ABBREVIATED_FILTER_OPERATOR_DISPLAY_MAP: Record<
  ProductAttributeFilterOperator,
  string
> = {
  eq: '',
  ne: 'Not',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
  contains: '',
  not_contains: 'Not',
  between: 'Between',
  starts_with: 'Starts with',
  not_starts_with: 'Not starts with',
  ends_with: 'Ends with',
  not_ends_with: 'Not ends with',
  empty: 'Empty',
  not_empty: 'Not empty',
  one_of: 'One of',
  desc_of_one_of: 'Descendant of one of'
}

/**
 * Datetime-specific display names for filter operations.
 */
const DATETIME_ATTRIBUTE_FILTER_OPERATOR_DISPLAY_MAP: Partial<
  Record<ProductAttributeFilterOperator, string>
> = {
  gt: 'Is after',
  lt: 'Is before',
  gte: 'Is after or on',
  lte: 'Is before or on'
}

export interface IProductAttributeFilter {
  operator: ProductAttributeFilterOperator
  attribute_field_id: string
  attribute_type: ProductAttributeType
  attribute_value?: ProductAttributeValueType
}

export interface IConfidenceScoreFilter {
  confidence_score: number
  operator: ProductAttributeFilterOperator
}

/**
 * Determines the display name of a given filter operator.
 *
 * @param attribute_type The attribute type of the filter.
 * @param operator Filter operator to obtain display name.
 * @param abbreviated Whether to return the abbreviated version (for top-level filter display).
 *
 * @returns The display name of the filter operator.
 */
export const filterOperatorDisplayName = (
  attribute_type: ProductAttributeType,
  operator: ProductAttributeFilterOperator,
  abbreviated: boolean = false
): string => {
  if (abbreviated) {
    return ABBREVIATED_FILTER_OPERATOR_DISPLAY_MAP[operator]
  }

  const datetimeDisplayName: string | undefined =
    attribute_type === ProductAttributeType.datetime
      ? DATETIME_ATTRIBUTE_FILTER_OPERATOR_DISPLAY_MAP?.[operator]
      : undefined
  return (
    datetimeDisplayName ??
    PRODUCT_ATTRIBUTE_FILTER_OPERATOR_DISPLAY_MAP[operator]
  )
}

export enum ProductFilterJoinOperation {
  and = 'and_',
  or = 'or_'
}

/**
 * Product attribute filter group.
 *
 * This is used to combine multiple filters with the
 * specified join operation.
 */
export interface IProductAttributeFilterGroup {
  /**
   * Filters related to product attribute values
   */
  filters: IProductAttributeFilter[]
  /**
   * Operation to join the filters
   */
  join_operation: ProductFilterJoinOperation
}

/**
 * Mapping from product attribute type to supported filter operators.
 */
export const ATTRIBUTE_TYPE_TO_SUPPORTED_FILTER_OPERATORS = {
  [ProductAttributeType.short_text]: [
    ProductAttributeFilterOperator.contains,
    ProductAttributeFilterOperator.not_contains,
    ProductAttributeFilterOperator.eq,
    ProductAttributeFilterOperator.ne,
    ProductAttributeFilterOperator.starts_with,
    ProductAttributeFilterOperator.not_starts_with,
    ProductAttributeFilterOperator.ends_with,
    ProductAttributeFilterOperator.not_ends_with,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.long_text]: [
    ProductAttributeFilterOperator.contains,
    ProductAttributeFilterOperator.not_contains,
    ProductAttributeFilterOperator.eq,
    ProductAttributeFilterOperator.ne,
    ProductAttributeFilterOperator.starts_with,
    ProductAttributeFilterOperator.not_starts_with,
    ProductAttributeFilterOperator.ends_with,
    ProductAttributeFilterOperator.not_ends_with,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.number]: [
    ProductAttributeFilterOperator.eq,
    ProductAttributeFilterOperator.ne,
    ProductAttributeFilterOperator.gt,
    ProductAttributeFilterOperator.lt,
    ProductAttributeFilterOperator.gte,
    ProductAttributeFilterOperator.lte
  ],
  [ProductAttributeType.datetime]: [
    ProductAttributeFilterOperator.between,
    ProductAttributeFilterOperator.lt,
    ProductAttributeFilterOperator.gt
  ],
  [ProductAttributeType.url]: [
    ProductAttributeFilterOperator.contains,
    ProductAttributeFilterOperator.not_contains,
    ProductAttributeFilterOperator.eq,
    ProductAttributeFilterOperator.ne,
    ProductAttributeFilterOperator.starts_with,
    ProductAttributeFilterOperator.not_starts_with,
    ProductAttributeFilterOperator.ends_with,
    ProductAttributeFilterOperator.not_ends_with,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.file_references]: [
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.media_urls]: [
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.multi_urls]: [
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.single_tag]: [
    ProductAttributeFilterOperator.eq,
    ProductAttributeFilterOperator.ne,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.multi_tags]: [
    ProductAttributeFilterOperator.contains,
    ProductAttributeFilterOperator.not_contains,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.status_tracker]: [
    ProductAttributeFilterOperator.contains,
    ProductAttributeFilterOperator.not_contains,
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ],
  [ProductAttributeType.taxonomy_category]: [ProductAttributeFilterOperator.eq],
  [ProductAttributeType.asset_references]: [
    ProductAttributeFilterOperator.empty,
    ProductAttributeFilterOperator.not_empty
  ]
} as const

export const TRANSLATABLE_ATTRIBUTE_TYPES = [
  ProductAttributeType.short_text,
  ProductAttributeType.long_text,
  ProductAttributeType.single_tag
]
